import React, { useEffect, useState } from 'react';
import { Check, Close } from '@icon-park/react';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  message: string;
  position: { x: number; y: number };
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  message,
  position,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // 延迟显示以触发动画
    requestAnimationFrame(() => setVisible(true));

    // 键盘快捷键支持
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onConfirm, onCancel]);

  return (
    <div
      className={`absolute z-50 transition-all duration-200 ${
        visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* 主体卡片 */}
      <div className="relative bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden backdrop-blur-sm">
        {/* 顶部装饰条 */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-green-500 to-blue-500"></div>
        
        {/* 内容区 */}
        <div className="px-3 py-2">
          {/* 消息 */}
          <div className="text-xs text-gray-700 font-medium mb-2 whitespace-nowrap">
            {message}
          </div>

          {/* 按钮组 */}
          <div className="flex gap-1.5">
            <button
              onClick={onConfirm}
              className="group flex items-center justify-center gap-1 px-2.5 py-1.5 bg-gradient-to-br from-green-500 to-green-600 text-white text-xs rounded-md hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm hover:shadow active:scale-95"
              title={`${t('dialog.confirm')} (Enter)`}
            >
              <Check size={14} theme="filled" strokeWidth={3} />
              <span className="font-medium">{t('dialog.confirm')}</span>
            </button>
            
            <button
              onClick={onCancel}
              className="group flex items-center justify-center gap-1 px-2.5 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-md hover:bg-gray-200 transition-all duration-200 hover:shadow-sm active:scale-95"
              title={`${t('dialog.cancel')} (Esc)`}
            >
              <Close size={14} theme="outline" strokeWidth={3} />
              <span className="font-medium">{t('dialog.cancel')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 箭头指示器 */}
      <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2">
        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white drop-shadow-sm"></div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
